import { type KeyboardEventHandler, useId, useState } from 'react';

import Button from '@poseidon/components/Input2/Button';
import FilterPopover from '@poseidon/components/Input2/FilterPopover';
import type { StepsRunCountFilterProps } from './types';

export default function StepsRunCountFilter({ size, count, operation, onChange }: StepsRunCountFilterProps) {
  const popoverId = useId();
  const anchorName = `--anchor-${popoverId.replace(/:/g, '-')}`;
  const [localCount, setLocalCount] = useState<string>(String(count));

  const stepsRunOperationOptions = [
    { label: 'at most', value: '<=' },
    { label: 'is', value: '==' },
    { label: 'at least', value: '>=' },
  ];

  const selectedOperation = stepsRunOperationOptions.find((option) => option.value === operation);

  const handleOperationChange = (value: string) => {
    const opValue = value as '==' | '<=' | '>=';
    const numCount = parseInt(localCount, 10);
    if (!Number.isNaN(numCount) && numCount >= 0) {
      onChange(numCount, opValue);
    } else {
      onChange(count, opValue);
    }
  };

  const handleCountChange = (newCount: string) => {
    setLocalCount(newCount);
    const numCount = parseInt(newCount, 10);
    if (!Number.isNaN(numCount) && numCount >= 0) {
      onChange(numCount, operation);
    }
  };

  const handleKeyDown: KeyboardEventHandler<HTMLInputElement> = (event) => {
    if (event.key === 'Enter') {
      const numCount = parseInt(localCount, 10);
      if (!Number.isNaN(numCount) && numCount >= 0) {
        onChange(numCount, operation);
      }
      event.currentTarget.blur();
    }
  };

  const buttonSize = size;

  return (
    <>
      <Button
        popoverTarget={popoverId}
        style={{ anchorName } as React.CSSProperties}
        text
        size={buttonSize}>
        {selectedOperation?.label}
      </Button>
      <FilterPopover
        popoverId={popoverId}
        anchorName={anchorName}
        size={buttonSize}
        items={stepsRunOperationOptions}
        selectedValue={operation}
        onSelect={handleOperationChange}
        filterable={false}
      />
      <input
        type="number"
        min="0"
        value={localCount}
        onChange={(event) => handleCountChange(event.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={() => {
          const numCount = parseInt(localCount, 10);
          if (!Number.isNaN(numCount) && numCount >= 0) {
            onChange(numCount, operation);
          } else {
            setLocalCount(String(count));
          }
        }}
        className="w-16 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-sm bg-white dark:bg-charade-900 text-gray-900 dark:text-gray-100"
      />
    </>
  );
}
